/*
 * SPELLROO: PREORDER confirmation in the cart.
 * Markup: snippets/sr-preorder-cart-gate.liquid (rendered in the side cart and the cart page).
 *
 * The rule: a cart with a "(PREORDER)" product can only go to checkout once the customer has
 * ticked the box, which saves the cart attribute "PREORDER terms accepted" = "Yes" (it shows on
 * the order). A cart without one never sees the box.
 *
 * What decides is the cart itself, not which boxes happen to be on the page. Liquid draws the box
 * from the cart at render time, but the page can fall behind the cart:
 *   - a quick-add puts a PREORDER item in while the drawer still shows an older copy (no box);
 *   - removing the PREORDER line on /cart re-renders only the item list (box left behind).
 * So this file keeps its own picture of the cart, updated from the theme's cart-update events and
 * from /cart.js after any other cart write, and hides/shows the box and locks checkout from that.
 *
 * Checkout clicks:
 *   - picture is current, nothing in flight: decide on the spot. Locked: block and point to the box.
 *     Otherwise the customer's own click goes through untouched, so the theme and the order-limit
 *     app (Pify, which ignores script-made clicks) handle it as usual.
 *   - something in flight (a quick-add, the tick being saved, a cart write not read back yet):
 *     block this click, catch up, then ask for one more click. Rare; usually under a second.
 *   - the cart needs the box but this view has none: redraw the drawer / reload /cart so it shows.
 *
 * Plain JS, no jQuery.
 */
(function () {
  var ATTR = 'PREORDER terms accepted';
  var CHECKOUT_SEL = '.button-checkout:not(.button-disable), button[name="checkout"], input[name="checkout"], a[href$="/checkout"]';
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var state = {
    known: false,        // false until we have read a cart; until then trust what Liquid drew
    hasPreorder: false,
    accepted: false,
    saving: null,        // promise while the tick is being saved
    reading: null,       // promise while /cart.js is being read
    dirty: false,        // a cart write finished that we have not read back yet
    takenAt: 0           // performance.now() when we last took a fresh cart
  };

  function boxes() { return Array.prototype.slice.call(document.querySelectorAll('[data-sr-preorder-accept]')); }
  function gates() { return Array.prototype.slice.call(document.querySelectorAll('[data-sr-preorder-gate]')); }
  function shown(el) { return !!(el && el.offsetParent); }

  function cartHasPreorder(cart) {
    return (cart.items || []).some(function (it) { return /\(PREORDER\)/.test(it.product_title || it.title || ''); });
  }

  function needsBox() { return state.known ? state.hasPreorder : gates().length > 0; }

  function isAccepted() {
    var list = boxes();
    if (list.length && list.every(function (b) { return b.checked; })) return true;
    return state.known && state.accepted && !list.some(function (b) { return !b.checked && shown(b); });
  }

  function isLocked() { return needsBox() && !isAccepted(); }

  function quickAdd() { return window.srQuickAdd || null; }
  function inFlight() {
    var qa = quickAdd();
    return !!(state.saving || state.reading || state.dirty || (qa && qa.busy && qa.busy()));
  }

  function apply() {
    var need = needsBox();
    var locked = isLocked();
    gates().forEach(function (gate) {
      // The cart no longer has a PREORDER item: the box left on the page goes away.
      gate.hidden = !need;
      gate.classList.toggle('is-locked', locked);
      if (!locked) {
        var hint = gate.querySelector('[data-sr-preorder-hint]');
        if (hint) hint.hidden = true;
      }
    });
    // An app script (order limit) strips the disabled attribute from the cart page
    // button, so the lock is shown with a class and enforced by the click guard below.
    document.querySelectorAll('[data-sr-checkout-lockable]').forEach(function (btn) {
      btn.classList.toggle('sr-checkout-locked', locked);
      if (locked) {
        if (!btn.hasAttribute('disabled')) btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
      } else if (!btn.classList.contains('sr-checkout-checking')) {
        if (btn.hasAttribute('disabled')) btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      }
    });
    document.querySelectorAll('[data-sr-express-lockable]').forEach(function (wrap) {
      wrap.hidden = locked;
    });
  }

  function takeCart(cart) {
    if (!cart || !cart.items) return;
    state.known = true;
    state.dirty = false;
    state.takenAt = performance.now();
    state.hasPreorder = cartHasPreorder(cart);
    state.accepted = !!(cart.attributes && cart.attributes[ATTR] === 'Yes');
    // Keep the boxes in step with what is saved (a box drawn before the tick was saved, etc.).
    if (!state.saving) boxes().forEach(function (b) { b.checked = state.accepted; });
    apply();
  }

  function readCart() {
    if (state.reading) return state.reading;
    state.dirty = false;
    state.reading = fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(takeCart)
      .catch(function () {})
      .then(function () { state.reading = null; });
    return state.reading;
  }

  function save(checked) {
    var attrs = {};
    attrs[ATTR] = checked ? 'Yes' : '';
    var p = fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      keepalive: true,   // still saved if the customer is already on the way to checkout
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) { return r.json(); }).then(function (cart) {
      if (state.saving === p) state.saving = null;
      takeCart(cart);
    }).catch(function () {
      if (state.saving === p) state.saving = null;
      state.dirty = true;
    });
    state.saving = p;
    return p;
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('[data-sr-preorder-accept]')) return;
    // Keep the side cart and the cart page in step.
    boxes().forEach(function (other) { other.checked = input.checked; });
    state.accepted = input.checked;
    apply();
    save(input.checked);
  });

  /* ---------- the cart changed somewhere: follow it ---------- */

  // Ella (drawer and /cart re-renders), quick-add and bundles send the fresh cart along.
  document.addEventListener('cart-update', function (e) {
    if (e.detail && e.detail.items) takeCart(e.detail);
  });
  document.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.cart && e.detail.cart.items) takeCart(e.detail.cart);
    else readCart();
  });
  // Any other cart write (another script, an app): read the cart back once it has finished.
  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver(function (list) {
        // Writes that finished before the cart we already have (e.g. the tick's own save, whose
        // answer we took) change nothing; counting them would hold the next Checkout click.
        var wrote = list.getEntries().some(function (en) { return CART_WRITE.test(en.name) && en.responseEnd > state.takenAt; });
        if (!wrote) return;
        state.dirty = true;
        // Quick-add's own writes: it sends the fresh cart (cart-update) once its queue is done.
        var qa = quickAdd();
        if (qa && qa.busy && qa.busy()) return;
        setTimeout(function () { if (state.dirty) readCart(); }, 50);
      }).observe({ type: 'resource', buffered: false });
    } catch (e) {}
  }
  // Back/forward cache, or back to this tab: the cart may have changed elsewhere (another tab).
  // Until the read is back, a Checkout click goes through the hold path.
  window.addEventListener('pageshow', function (e) { if (e.persisted) { state.dirty = true; readCart(); } });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') return;
    state.dirty = true;
    readCart();
  });

  /* ---------- checkout clicks ---------- */

  function showHint(trigger) {
    var scope = trigger.closest('.previewCart, .cart, form, body') || document;
    var gate = scope.querySelector('[data-sr-preorder-gate]') || document.querySelector('[data-sr-preorder-gate]');
    if (!gate || gate.hidden) return false;
    var hint = gate.querySelector('[data-sr-preorder-hint]');
    if (hint) hint.hidden = false;
    gate.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return true;
  }

  // The cart needs the box but this view does not have one: draw it.
  function bringBoxIn(trigger) {
    var inDrawer = trigger.closest('#halo-cart-sidebar');
    var qa = quickAdd();
    if (inDrawer && qa && qa.refreshDrawer) {
      qa.refreshDrawer().then(function () { apply(); showHint(trigger); });
    } else if (document.body.classList.contains('template-cart')) {
      window.location.reload();
    } else if (inDrawer && window.sharedFunctions && window.sharedFunctions.updateSidebarCart) {
      fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); })
        .then(function (cart) { window.sharedFunctions.updateSidebarCart(cart); });
    } else {
      window.location.href = '/cart';
    }
  }

  // Only a class and aria-busy (no label swap): the order-limit app re-clones checkout buttons,
  // and a swapped label could be copied onto the clone and never come back. On finish every
  // button still marked is cleared, clones included.
  var checking = false;
  function setChecking(btn, on) {
    checking = on;
    if (on) {
      btn.classList.add('sr-checkout-checking');
      btn.setAttribute('aria-busy', 'true');
      return;
    }
    document.querySelectorAll('.sr-checkout-checking').forEach(function (el) {
      el.classList.remove('sr-checkout-checking');
      el.removeAttribute('aria-busy');
    });
  }

  // Window, capture phase: runs before the theme's and the order-limit app's own checkout handlers.
  window.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var trigger = event.target.closest(CHECKOUT_SEL);
    if (!trigger) return;

    if (!inFlight()) {
      if (!isLocked()) return;              // let the click through untouched
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!showHint(trigger)) bringBoxIn(trigger);
      return;
    }

    // Something is still changing the cart: hold this click, catch up, ask for one more.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checking) return;
    setChecking(trigger, true);
    var qa = quickAdd();
    Promise.resolve(qa && qa.whenIdle ? qa.whenIdle() : null)
      .then(function () { return state.saving; })
      .then(readCart)
      .then(function () {
        setChecking(trigger, false);
        apply();
        if (isLocked() && !showHint(trigger)) bringBoxIn(trigger);
      });
  }, true);

  /* ---------- start ---------- */

  var queued = false;
  function queueApply() {
    if (queued) return;
    queued = true;
    // setTimeout rather than requestAnimationFrame: rAF does not run in background tabs.
    setTimeout(function () {
      queued = false;
      apply();
    }, 0);
  }

  function start() {
    apply();
    // The side cart is re-rendered by AJAX. Only watch childList: watching the
    // disabled attribute would fight the app that strips it (endless loop).
    new MutationObserver(queueApply).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
